/**
 * @file AudioPlayer.tsx
 * @brief Compact audio player primitives — `AudioPlayBadge` for inline
 *        play/pause toggles inside transcript bubbles,
 *        `AudioScrubber` for a short seekable note/clip, and
 *        `AudioPlayerCard` for the full-width session-level audio
 *        controls at the top of the session detail dialog.
 *
 * @details
 * The web panel needs to expose the same audio assets the mobile app
 * already plays back:
 *   - per-utterance original (source) and translated (target) audio
 *   - the full mixed-down conversation audio attached to the session
 *
 * Both surfaces share a tiny play/stop state machine implemented with
 * a single shared `<audio>` element per component instance. Multiple
 * `AudioPlayBadge`s on the same screen coordinate via a small module-
 * level registry so starting one stops any other badge that's
 * currently playing — same single-stream UX as the mobile
 * `usePlayback` hook.
 *
 * Sources are signed URLs (1h TTL on `src/tgt_audio_url`) emitted by
 * `BACKEND/src/features/admin/service.js::getSessionByIdAdmin`; the
 * panel never persists or rewrites them.
 *
 * @dependencies
 * - lucide-react: Play / Pause / Loader icons
 * - components/ui/Button
 * - shared/theme/cn
 *
 * @references
 * - BACKEND/src/features/admin/service.js (signed audio URL emitter)
 * - FRONTEND/src/screens/S2SInterpreter/hooks/usePlayback.ts
 *
 * @author DES
 * @date 26-05-2026
 */

import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { LoaderCircle, Pause, Play, RotateCcw, RotateCw } from "lucide-react";
import { cn } from "@/shared/theme/cn";

// ----------------------------------------------------------------------
// Cross-instance "only one badge plays at a time" coordinator. Each
// running player registers a `stop` callback and the next one to press
// play calls every other registered stopper before kicking off its own
// stream. Mirrors the single-stream policy of the mobile playback hook.
// ----------------------------------------------------------------------
type Stopper = () => void;
const stoppers = new Set<Stopper>();
function stopAllOthers(self: Stopper) {
  stoppers.forEach((s) => {
    if (s !== self) s();
  });
}

// ----------------------------------------------------------------------
// AudioPlayBadge — small pill-style play/stop button used inside
// transcript bubbles next to the original / translated text.
// ----------------------------------------------------------------------
interface AudioPlayBadgeProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  src?: string | null;
  /** Visual variant — `original` is brand-tinted, `translated` is
   *  the muted complement. Either falls back to a disabled state when
   *  `src` is missing so the layout stays stable. */
  variant?: "original" | "translated";
  label?: string;
  icon?: ReactNode;
  /** Seek offset (seconds) to start playback from — used for "voice stamp"
   *  snippets that jump into the full recording at a bookmark. (08-07-2026) */
  startSecs?: number | null;
  /** When set, auto-stop after this many seconds — plays a bounded snippet
   *  rather than the whole recording (mobile plays a ~6s voice stamp). */
  clipSecs?: number | null;
  /** Extra classes applied while audio is actively playing (10-07-2026). */
  playingClassName?: string;
  /** Fires when playback starts/stops — used by clickable parent rows. (10-07-2026) */
  onPlayingChange?: (playing: boolean) => void;
}

export const AudioPlayBadge = forwardRef<HTMLButtonElement, AudioPlayBadgeProps>(
  function AudioPlayBadge(
    {
      src,
      variant = "original",
      label,
      icon,
      startSecs,
      clipSecs,
      className,
      playingClassName,
      onPlayingChange,
      disabled,
      ...rest
    },
    ref,
  ) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const clipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Removes the `timeupdate` watcher that bounds the clip. */
  const clipWatcherRef = useRef<(() => void) | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "playing">("idle");

  const clearClipTimer = useCallback(() => {
    if (clipTimerRef.current) {
      clearTimeout(clipTimerRef.current);
      clipTimerRef.current = null;
    }
    if (clipWatcherRef.current) {
      clipWatcherRef.current();
      clipWatcherRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    clearClipTimer();
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.currentTime = 0;
    }
    setState("idle");
  }, [clearClipTimer]);

  // Cleanup on unmount — also de-registers from the cross-instance
  // stopper set so we don't leak callbacks bound to dead elements.
  useEffect(() => {
    const stopper = stop;
    stoppers.add(stopper);
    return () => {
      stoppers.delete(stopper);
      clearClipTimer();
      const a = audioRef.current;
      if (a) {
        a.pause();
        a.src = "";
      }
    };
  }, [stop, clearClipTimer]);

  useEffect(() => {
    onPlayingChange?.(state === "playing" || state === "loading");
  }, [state, onPlayingChange]);

  const handleClick = async () => {
    if (!src) return;
    if (state === "playing") {
      stop();
      return;
    }
    stopAllOthers(stop);
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = "none";
      audioRef.current.addEventListener("ended", () => setState("idle"));
      audioRef.current.addEventListener("error", () => setState("idle"));
    }
    const a = audioRef.current;
    if (a.src !== src) {
      a.src = src;
    }

    // ------------------------------------------------------------------
    // Seek BEFORE the first sample is audible, and measure the clip in
    // PLAYED seconds. (23-08-2026)
    //
    // The previous version deferred the seek to `loadedmetadata` and started
    // playing regardless, so with `preload="none"` the element could begin at
    // 00:00 and jump to the anchor a moment later — the listener heard the
    // meeting's opening as the evidence for an item. And the stop was a
    // wall-clock `setTimeout` started at `play()`, so buffering was charged to
    // the clip: a stalled stream ended the snippet before the anchored words
    // were reached. Now the metadata load is awaited, the seek lands first, and
    // the end is a POSITION in the media, not a moment on the clock.
    // Related: FRONTEND/.../hooks/useMeetingDetailsContent.ts
    // ------------------------------------------------------------------
    const from = typeof startSecs === "number" && startSecs > 0 ? startSecs : 0;

    setState("loading");
    try {
      if (from > 0 && a.readyState < 1 /* HAVE_METADATA */) {
        await new Promise<void>((resolve, reject) => {
          const done = () => {
            a.removeEventListener("loadedmetadata", onMeta);
            a.removeEventListener("error", onErr);
          };
          const onMeta = () => {
            done();
            resolve();
          };
          const onErr = () => {
            done();
            reject(new Error("audio failed to load"));
          };
          a.addEventListener("loadedmetadata", onMeta);
          a.addEventListener("error", onErr);
          a.load();
        });
      }
      if (from > 0) {
        try {
          a.currentTime = from;
        } catch {
          /* seeking not ready — best effort */
        }
      }

      await a.play();
      setState("playing");

      // Bounded snippet: stop at the END OF THE SPAN so it plays a stamp, not
      // the whole meeting — parity with the mobile voice-stamp controller.
      clearClipTimer();
      if (typeof clipSecs === "number" && clipSecs > 0) {
        const endAt = from + clipSecs;
        const onTime = () => {
          if (a.currentTime >= endAt) stop();
        };
        a.addEventListener("timeupdate", onTime);
        clipWatcherRef.current = () => a.removeEventListener("timeupdate", onTime);
        // Safety net only: `timeupdate` stops firing if the element stalls, and
        // a snippet that never ends would keep playing the rest of the meeting.
        clipTimerRef.current = setTimeout(() => stop(), (clipSecs + 15) * 1000);
      }
    } catch {
      setState("idle");
    }
  };

  const isDisabled = disabled || !src;
  const tone =
    variant === "original"
      ? "bg-brand-soft text-brand hover:bg-brand-soft/80"
      : "bg-secondary text-secondary-foreground hover:bg-secondary/70";

  return (
    <button
      ref={ref}
      type="button"
      onClick={handleClick}
      disabled={isDisabled}
      aria-label={
        label ?? (variant === "original" ? "Play original" : "Play translated")
      }
      data-state={state}
      className={cn(
        "inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-[11px] font-semibold uppercase tracking-wider transition-colors",
        tone,
        isDisabled && "cursor-not-allowed opacity-40 hover:bg-transparent",
        className,
        (state === "playing" || state === "loading") && playingClassName,
      )}
      {...rest}
    >
      <span className="flex h-3.5 w-3.5 items-center justify-center">
        {state === "loading" ? (
          <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
        ) : state === "playing" ? (
          <Pause className="h-3.5 w-3.5" />
        ) : (
          (icon ?? <Play className="h-3 w-3 fill-current" />)
        )}
      </span>
      {label ? <span>{label}</span> : null}
    </button>
  );
  },
);
AudioPlayBadge.displayName = "AudioPlayBadge";

// ----------------------------------------------------------------------
// Clock — always M:SS so 0:07 and 1:09 line up. formatDurationSeconds
// would print "7s" under a minute, which jumps width as the playhead moves.
// ----------------------------------------------------------------------
function formatClock(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// ----------------------------------------------------------------------
// AudioScrubber — play + a SHORT seek bar. The native `<audio controls>`
// chrome is a full-rail grey slab (Safari especially); a one-minute note
// does not need that. Cap the track so it stays a control, not a banner.
// Related: WEBPANEL notes NoteDetailPage, mobile NotePlaybackButton.
// ----------------------------------------------------------------------
export function AudioScrubber({
  src,
  className,
}: {
  src: string;
  className?: string;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const draggingRef = useRef(false);
  const [state, setState] = useState<"idle" | "loading" | "playing">("idle");
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  const stop = useCallback(() => {
    audioRef.current?.pause();
    setState("idle");
  }, []);

  useEffect(() => {
    const a = new Audio();
    a.preload = "metadata";
    a.src = src;
    audioRef.current = a;

    const onMeta = () => {
      setDuration(Number.isFinite(a.duration) ? a.duration : 0);
    };
    const onTime = () => {
      if (!draggingRef.current) setCurrent(a.currentTime);
    };
    const onEnded = () => {
      setState("idle");
      setCurrent(Number.isFinite(a.duration) ? a.duration : 0);
    };

    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("durationchange", onMeta);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnded);
    a.addEventListener("error", () => setState("idle"));
    stoppers.add(stop);

    return () => {
      stoppers.delete(stop);
      a.pause();
      a.src = "";
      audioRef.current = null;
    };
  }, [src, stop]);

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (state === "playing") {
      stop();
      return;
    }
    stopAllOthers(stop);
    if (duration > 0 && current >= duration - 0.25) {
      a.currentTime = 0;
      setCurrent(0);
    }
    setState("loading");
    try {
      await a.play();
      setState("playing");
    } catch {
      setState("idle");
    }
  };

  const seek = (value: number) => {
    setCurrent(value);
    const a = audioRef.current;
    if (a && Number.isFinite(value)) {
      try {
        a.currentTime = value;
      } catch {
        /* seeking not ready */
      }
    }
  };

  const max = duration > 0 ? duration : 0;

  return (
    <div className={cn("flex max-w-sm items-center gap-2", className)}>
      <button
        type="button"
        onClick={() => void toggle()}
        aria-label={state === "playing" ? "Pause recording" : "Play recording"}
        className={cn(
          "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-foreground/70 hover:bg-brand-soft hover:text-brand",
          state === "playing" && "bg-brand text-brand-foreground hover:bg-brand hover:text-brand-foreground",
        )}
      >
        {state === "loading" ? (
          <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
        ) : state === "playing" ? (
          <Pause className="h-3.5 w-3.5" />
        ) : (
          <Play className="h-3 w-3 fill-current" />
        )}
      </button>
      <span className="shrink-0 text-right text-[11px] tabular-nums text-muted-foreground">
        {formatClock(current)}
      </span>
      <input
        type="range"
        min={0}
        max={max}
        step={0.1}
        value={max > 0 ? Math.min(current, max) : 0}
        disabled={max <= 0}
        aria-label="Seek"
        onPointerDown={() => {
          draggingRef.current = true;
        }}
        onPointerUp={() => {
          draggingRef.current = false;
        }}
        onBlur={() => {
          draggingRef.current = false;
        }}
        onChange={(e) => seek(Number(e.target.value))}
        className="h-1 min-w-0 flex-1 cursor-pointer appearance-none rounded-full bg-border accent-brand disabled:cursor-not-allowed [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-brand [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand"
      />
      <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
        {formatClock(duration)}
      </span>
    </div>
  );
}

// ----------------------------------------------------------------------
// H:MM:SS for recordings past an hour (a 96-minute meeting reads 1:36:17,
// not 96:17). Under an hour it is the same M:SS as formatClock.
// ----------------------------------------------------------------------
function formatLongClock(seconds: number, showHours: boolean): string {
  const total = Math.max(0, Math.floor(seconds));
  if (!showHours) return formatClock(total);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

const SKIPS = [-30, -10, 10, 30] as const;
const RATES = [1, 1.25, 1.5, 2] as const;

// ----------------------------------------------------------------------
// AudioPlayerCard — full session/meeting-level player. Custom chrome
// instead of native `<audio controls>`: in the meeting detail rail the
// native control squeezed its timeline to a ~40px sliver, so a 90-minute
// meeting could not be scrubbed. Here the seek bar owns a full-width row
// with a tall hit area and a hover time preview, plus ±10s/±30s skips
// and a playback-speed toggle. Renders nothing when `src` is empty so the
// parent doesn't have to branch. (25-09-2026)
// ----------------------------------------------------------------------
interface AudioPlayerCardProps {
  src?: string | null;
  /** Pass `null` when the parent already named the recording (a GridBand
   *  heading) — otherwise this card draws a second title inside itself. */
  title?: string | null;
  subtitle?: string;
  className?: string;
  /** Fallback length (seconds) for files whose container reports no
   *  finite duration (MediaRecorder WebM reports Infinity). */
  durationHint?: number | null;
  /** Page-wide keys: ←/→ skip 10s (Shift: 30s), Space plays/pauses.
   *  Ignored while typing in a field or while a dialog this player is
   *  not inside is open. One per page, or both would react. (25-09-2026) */
  keyboardShortcuts?: boolean;
}

export function AudioPlayerCard({
  src,
  title = "Full conversation audio",
  subtitle,
  className,
  durationHint,
  keyboardShortcuts = false,
}: AudioPlayerCardProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const rangeRef = useRef<HTMLInputElement | null>(null);
  // Latest key handler — reassigned every render so it sees fresh state
  // without re-binding the window listener.
  const keyRef = useRef<((e: KeyboardEvent) => void) | null>(null);
  const draggingRef = useRef(false);
  const [state, setState] = useState<"idle" | "loading" | "playing">("idle");
  const [current, setCurrent] = useState(0);
  const [mediaDuration, setMediaDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [rate, setRate] = useState<number>(1);
  const [hover, setHover] = useState<{ x: number; t: number } | null>(null);

  const stop = useCallback(() => {
    audioRef.current?.pause();
    setState("idle");
  }, []);

  useEffect(() => {
    if (!src) return;
    const a = new Audio();
    a.preload = "metadata";
    a.src = src;
    audioRef.current = a;
    setCurrent(0);
    setMediaDuration(0);
    setBuffered(0);
    setState("idle");

    const onMeta = () => {
      setMediaDuration(Number.isFinite(a.duration) ? a.duration : 0);
    };
    const onTime = () => {
      if (!draggingRef.current) setCurrent(a.currentTime);
    };
    const onProgress = () => {
      const b = a.buffered;
      if (b.length > 0) setBuffered(b.end(b.length - 1));
    };
    const onEnded = () => setState("idle");
    const onWaiting = () => setState((s) => (s === "playing" ? "loading" : s));
    const onPlaying = () => setState("playing");
    const onPause = () => setState("idle");

    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("durationchange", onMeta);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("progress", onProgress);
    a.addEventListener("ended", onEnded);
    a.addEventListener("waiting", onWaiting);
    a.addEventListener("playing", onPlaying);
    a.addEventListener("pause", onPause);
    a.addEventListener("error", () => setState("idle"));
    stoppers.add(stop);

    return () => {
      stoppers.delete(stop);
      a.pause();
      a.src = "";
      audioRef.current = null;
    };
  }, [src, stop]);

  // Keep the chosen speed across play/pause and seeks.
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = rate;
  }, [rate, src]);

  useEffect(() => {
    if (!keyboardShortcuts || !src) return;
    const onKey = (e: KeyboardEvent) => keyRef.current?.(e);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [keyboardShortcuts, src]);

  if (!src) return null;

  const duration =
    mediaDuration > 0 ? mediaDuration : Math.max(0, Number(durationHint) || 0);
  const showHours = duration >= 3600;
  const pct = duration > 0 ? Math.min(100, (current / duration) * 100) : 0;
  const bufPct = duration > 0 ? Math.min(100, (buffered / duration) * 100) : 0;

  const seek = (value: number) => {
    const clamped = Math.max(0, duration > 0 ? Math.min(value, duration) : value);
    setCurrent(clamped);
    const a = audioRef.current;
    if (a && Number.isFinite(clamped)) {
      try {
        a.currentTime = clamped;
      } catch {
        /* seeking not ready */
      }
    }
  };

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (state !== "idle") {
      stop();
      return;
    }
    stopAllOthers(stop);
    if (duration > 0 && current >= duration - 0.25) seek(0);
    setState("loading");
    try {
      a.playbackRate = rate;
      await a.play();
      setState("playing");
    } catch {
      setState("idle");
    }
  };

  const timeAt = (clientX: number) => {
    const el = trackRef.current;
    if (!el || duration <= 0) return null;
    const r = el.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - r.left, 0), r.width);
    return { x, t: (x / r.width) * duration };
  };

  keyRef.current = (e: KeyboardEvent) => {
    if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
    const t = e.target instanceof HTMLElement ? e.target : null;
    // Our own seek bar is an <input>, but its native arrow step is 1s;
    // take its arrows over. Any other field keeps its keys for typing.
    if (t && t !== rangeRef.current) {
      if (
        t.isContentEditable ||
        t.closest("input, textarea, select, [contenteditable='true']")
      ) {
        return;
      }
    }
    const dialog = document.querySelector('[role="dialog"], [role="alertdialog"]');
    if (dialog && !dialog.contains(rootRef.current)) return;

    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      e.preventDefault();
      const step = e.shiftKey ? 30 : 10;
      seek(current + (e.key === "ArrowLeft" ? -step : step));
    } else if (e.key === " " || e.code === "Space") {
      // A focused button/link already activates on Space; don't double it.
      if (t && t !== rangeRef.current && t.closest("button, a, [role='button']")) return;
      e.preventDefault();
      void toggle();
    }
  };

  const cycleRate = () => {
    const i = RATES.indexOf(rate as (typeof RATES)[number]);
    setRate(RATES[(i + 1) % RATES.length]);
  };

  return (
    <div
      ref={rootRef}
      className={cn(
        "flex flex-col gap-3 rounded-3xl border bg-secondary/40 p-4",
        className,
      )}
    >
      {title ? (
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{title}</p>
          {subtitle ? (
            <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
          ) : null}
        </div>
      ) : null}

      {/* Seek bar — full width, 24px tall hit area around a 6px track. The
          transparent range input on top gives drag + arrow keys for free. */}
      <div className="flex flex-col gap-1">
        <div
          ref={trackRef}
          className="group relative h-6 w-full"
          onPointerMove={(e) => setHover(timeAt(e.clientX))}
          onPointerLeave={() => setHover(null)}
        >
          <div className="pointer-events-none absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 overflow-hidden rounded-full bg-border">
            <div
              className="absolute inset-y-0 left-0 bg-foreground/15"
              style={{ width: `${bufPct}%` }}
            />
            <div
              className="absolute inset-y-0 left-0 bg-brand"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div
            className="pointer-events-none absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand shadow ring-2 ring-background transition-transform group-hover:scale-110"
            style={{ left: `${pct}%` }}
          />
          {hover ? (
            <div
              className="pointer-events-none absolute -top-6 -translate-x-1/2 rounded-md bg-foreground px-1.5 py-0.5 text-[10px] tabular-nums text-background"
              style={{ left: hover.x }}
            >
              {formatLongClock(hover.t, showHours)}
            </div>
          ) : null}
          <input
            ref={rangeRef}
            type="range"
            min={0}
            max={duration > 0 ? duration : 0}
            step={1}
            value={duration > 0 ? Math.min(current, duration) : 0}
            disabled={duration <= 0}
            aria-label="Seek"
            aria-valuetext={formatLongClock(current, showHours)}
            onPointerDown={() => {
              draggingRef.current = true;
            }}
            onPointerUp={() => {
              draggingRef.current = false;
            }}
            onBlur={() => {
              draggingRef.current = false;
            }}
            onChange={(e) => seek(Number(e.target.value))}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
          />
        </div>
        <div className="flex items-center justify-between text-[11px] tabular-nums text-muted-foreground">
          <span>{formatLongClock(current, showHours)}</span>
          <button
            type="button"
            onClick={cycleRate}
            aria-label={`Playback speed ${rate}x`}
            title="Playback speed"
            className="inline-flex h-6 min-w-10 items-center justify-center rounded-full bg-secondary px-2 text-[11px] font-semibold tabular-nums text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {rate}x
          </button>
          <span>
            {duration > 0 ? `-${formatLongClock(duration - current, showHours)}` : "--:--"}
          </span>
        </div>
      </div>

      {/* Transport — skips flank the play button, centred. Speed lives in
          the time row, away from the rail's edge. */}
      <div className="flex items-center justify-center">
        <div className="flex items-center gap-1">
          {SKIPS.slice(0, 2).map((d) => (
            <SkipButton
              key={d}
              delta={d}
              hint={keyboardShortcuts ? (d === -10 ? "←" : "Shift ←") : undefined}
              onClick={() => seek(current + d)}
            />
          ))}
          <button
            type="button"
            onClick={() => void toggle()}
            aria-label={state === "playing" ? "Pause recording" : "Play recording"}
            title={keyboardShortcuts ? "Play / pause (Space)" : undefined}
            className="mx-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand text-brand-foreground shadow-sm transition-transform hover:scale-105 active:scale-95"
          >
            {state === "loading" ? (
              <LoaderCircle className="h-5 w-5 animate-spin" />
            ) : state === "playing" ? (
              <Pause className="h-5 w-5 fill-current" />
            ) : (
              <Play className="ml-0.5 h-5 w-5 fill-current" />
            )}
          </button>
          {SKIPS.slice(2).map((d) => (
            <SkipButton
              key={d}
              delta={d}
              hint={keyboardShortcuts ? (d === 10 ? "→" : "Shift →") : undefined}
              onClick={() => seek(current + d)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function SkipButton({
  delta,
  hint,
  onClick,
}: {
  delta: number;
  hint?: string;
  onClick: () => void;
}) {
  const back = delta < 0;
  const Icon = back ? RotateCcw : RotateCw;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${back ? "Back" : "Forward"} ${Math.abs(delta)} seconds`}
      title={`${back ? "Back" : "Forward"} ${Math.abs(delta)}s${hint ? ` (${hint})` : ""}`}
      className="inline-flex h-9 shrink-0 items-center gap-0.5 rounded-full px-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
    >
      {back ? <Icon className="h-4 w-4" /> : null}
      <span className="text-[11px] font-semibold tabular-nums">{Math.abs(delta)}</span>
      {back ? null : <Icon className="h-4 w-4" />}
    </button>
  );
}
